/**
 * useMarpTemplates — TanStack Query wrapper around the Wave 4 BE-Templates
 * surface (`GET /api/v1/templates/marp`).
 *
 * Returns the merged set of built-in + custom templates pre-shaped for the
 * `<MarpTemplatePicker>` (which expects a flat `MarpTemplateOption[]`).
 *
 * Fallback semantics: when the BE returns a 404 (Wave 4 still rolling out)
 * we degrade to the four built-in slugs hard-coded here so the picker stays
 * functional and the user can at least toggle between built-ins. This
 * mirrors the BE-Core templates registry in
 * `src/services/presentations_marp/marp_themes/__init__.py`.
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import * as React from "react";

import { ApiError } from "@ando/core/api";

import {
  listMarpTemplates,
  type MarpTemplateListResponse,
} from "./api";

import type { MarpTemplateOption } from "@ando/ui/editors";

/** Source of truth for the four built-in slugs (mirrors the BE registry). */
export const MARP_BUILTIN_TEMPLATES: ReadonlyArray<MarpTemplateOption> = [
  {
    slug: "editorial_ando",
    label: "Editoriale Ando",
    description: "Default · cream surface, brand orange accents",
    scope: "builtin",
  },
  {
    slug: "consulting_mck",
    label: "Consulting · McKinsey",
    description: "Action titles, navy + light-blue accents",
    scope: "builtin",
  },
  {
    slug: "pitch_sequoia",
    label: "Pitch · Sequoia",
    description: "Centered hero, Sequoia red on white",
    scope: "builtin",
  },
  {
    slug: "keynote_minimal",
    label: "Minimal · Keynote",
    description: "Dark navy surface, white type, large hero",
    scope: "builtin",
  },
] as const;

export const DEFAULT_MARP_TEMPLATE = "editorial_ando" as const;

export function marpTemplatesQueryKey(): readonly unknown[] {
  return ["marp-templates"] as const;
}

async function fetchOrFallback(): Promise<MarpTemplateListResponse> {
  try {
    return await listMarpTemplates();
  } catch (err) {
    // 404 / not-yet-deployed → built-ins-only fallback so the picker
    // stays alive while BE-Templates ships behind. Any other error
    // bubbles up so the picker can show an error state.
    if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
      return {
        builtins: MARP_BUILTIN_TEMPLATES.map((t) => t.slug),
        customs: [],
      };
    }
    throw err;
  }
}

/**
 * Live template list, BE-backed with a builtins-only fallback.
 *
 * Staleness: 5 minutes — template uploads are rare; we balance freshness
 * against the cost of refetching on every chat-open.
 */
export function useMarpTemplates(): UseQueryResult<MarpTemplateListResponse> {
  return useQuery<MarpTemplateListResponse>({
    queryKey: marpTemplatesQueryKey(),
    queryFn: fetchOrFallback,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    retry: 1,
  });
}

/**
 * Helper — fold the raw BE list into the flat `<MarpTemplatePicker>`
 * option array (builtins first, customs after). Memoized at the call
 * site via `React.useMemo`.
 */
export function buildPickerOptions(
  data: MarpTemplateListResponse | undefined,
): ReadonlyArray<MarpTemplateOption> {
  if (!data) return MARP_BUILTIN_TEMPLATES;

  // Map known builtins through the static metadata so the user gets a
  // human label + description; any unknown slug the BE adds in the
  // future degrades to a "slug as label" entry rather than dropping.
  const byBuiltinSlug = new Map(MARP_BUILTIN_TEMPLATES.map((t) => [t.slug, t]));
  const builtins: MarpTemplateOption[] = data.builtins.map((slug) => {
    const known = byBuiltinSlug.get(slug);
    return known ?? { slug, label: slug, scope: "builtin" as const };
  });

  const customs: MarpTemplateOption[] = data.customs.map((row) => ({
    slug: row.slug,
    label: row.slug,
    description: row.description,
    scope: "custom" as const,
  }));

  return [...builtins, ...customs];
}

/**
 * Memo-friendly hook variant — returns the flattened picker options
 * alongside the raw query so callers that need to know about loading /
 * error state can branch on it.
 */
export function useMarpPickerOptions(): {
  options: ReadonlyArray<MarpTemplateOption>;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useMarpTemplates();
  const options = React.useMemo(
    () => buildPickerOptions(query.data),
    [query.data],
  );
  return {
    options,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
